import { uniqueId } from "lodash"
import React, { useEffect, type ReactNode } from "react"
import { Tooltip } from "../Tooltip"
import { cn } from "~/utils/helpers"
import { Badge } from "../Badge"

/**
 * Simple table with column headers only (no row headers).
 * Use for: Lists, simple tables, column-based data (e.g., user lists, metrics, SQL results)
 * For cross-tabulated data with both row AND column headers, use MiniMatrix instead.
 *
 * TODO: Refactor to use pure CSS Grid approach (like MiniMatrix) instead of useEffect DOM manipulation
 */
export default function MiniTable({
  headers,
  rows,
  className,
  emptyText = "No data",
  noBorder = false,
  gridTemplateColumns,
}: {
  headers: string[]
  rows: Array<Array<string | number | ReactNode | undefined>>
  className?: string
  emptyText?: string
  noBorder?: boolean
  gridTemplateColumns?: string
}) {
  const hasRows = rows.length > 0
  const allRows = [...rows, headers]
  const longestRow = Math.max(...allRows.map((row) => row.length))
  const adjustedHeaders = [
    ...headers,
    ...Array.from<string>({ length: longestRow - headers.length }).fill(""),
  ]
  const adjustedRows = Array.from(
    rows.map((row) => [
      ...row,
      ...Array.from<string | undefined>({ length: longestRow - row.length }),
    ]),
  )

  const gridUUID = uniqueId("minitable_grid")
  useEffect(() => {
    const gridElements = document.querySelectorAll<HTMLDivElement>(`#${gridUUID}`)
    gridElements.forEach((node) => {
      node.style["gridTemplateColumns"] =
        gridTemplateColumns ?? `repeat(${longestRow}, minmax(0, 1fr))`
    })
  }, [longestRow, gridTemplateColumns])

  return (
    <div
      className={cn(
        "flex flex-col bg-border-base rounded-lg",
        hasRows && "gap-[1px]",
        noBorder ? "" : "p-[1px]",
        className,
      )}
    >
      {headers.length > 0 ? (
        <div className={cn("grid gap-[1px] *:px-2 *:py-[6px] rounded-lg")} id={gridUUID}>
          {adjustedHeaders.map((header, i) => (
            <TableHeader key={i} header={header} hasRows={hasRows} />
          ))}
        </div>
      ) : (
        <></>
      )}
      <div className="flex flex-col overflow-y-auto">
        <div className={cn("grid gap-[1px] *:px-2 *:py-[6px]")} id={gridUUID}>
          {adjustedRows.map((row, i) => (
            <TableRow
              key={i}
              row={row}
              lastRow={i === adjustedRows.length - 1}
              emptyText={emptyText}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

const TableHeader = ({ header, hasRows }: { header: string; hasRows: boolean }) => {
  return (
    <Tooltip title={header}>
      <div
        className={cn(
          "bg-subtle truncate first:rounded-tl-lg last:rounded-tr-lg paragraph-base text-fg-subtle",
          !hasRows && "first:rounded-bl-lg last:rounded-br-lg",
        )}
      >
        {header}
      </div>
    </Tooltip>
  )
}

const TableRow = ({
  row,
  lastRow,
  emptyText,
}: {
  row: Array<string | number | ReactNode | undefined>
  lastRow: boolean
  emptyText: string
}) => {
  return (
    <>
      {row.map((col, colIndex) => (
        <Tooltip
          title={`${col == null || React.isValidElement(col) ? "" : col}`}
          key={`${colIndex}`}
        >
          {col == null ? (
            <div
              className={`text-nowrap truncate bg-base last:rounded-br-lg paragraph-base text-fg-muted content-center`}
              style={{ borderBottomLeftRadius: lastRow && colIndex === 0 ? "8px" : "0px" }}
            >
              {emptyText}
            </div>
          ) : React.isValidElement(col) ? (
            col
          ) : (
            <div
              className={`text-nowrap truncate bg-base last:rounded-br-lg paragraph-base text-fg-base content-center`}
              style={{ borderBottomLeftRadius: lastRow && colIndex === 0 ? "8px" : "0px" }}
            >
              {col}
            </div>
          )}
        </Tooltip>
      ))}
    </>
  )
}

export const SignalIcon = ({
  variant,
  preferLow,
}: {
  variant: "empty" | "low" | "medium" | "full"
  preferLow?: boolean
}) => {
  const grayBg = "bg-border-strong"
  let coloredBg: string
  if (preferLow) {
    coloredBg =
      variant === "low" ? "bg-green-500" : variant === "medium" ? "bg-orange-500" : "bg-red-500"
  } else {
    coloredBg =
      variant === "low" ? "bg-red-500" : variant === "medium" ? "bg-orange-500" : "bg-green-500"
  }
  const signalStrength =
    variant === "empty" ? 0 : variant === "low" ? 1 : variant === "medium" ? 2 : 3
  return (
    <div className={`w-4 h-4 flex flex-row gap-0.25 items-baseline`}>
      <div className={`w-1 h-2 rounded-[999px] ${signalStrength < 1 ? grayBg : coloredBg}`} />
      <div className={`w-1 h-3 rounded-[999px] ${signalStrength < 2 ? grayBg : coloredBg}`} />
      <div className={`w-1 h-4 rounded-[999px] ${signalStrength < 3 ? grayBg : coloredBg}`} />
    </div>
  )
}

export const BadgeRatingCell = ({
  value,
  onClick,
  className,
  preferLow,
  thresholds = [0.25, 0.5],
}: {
  value: number | undefined
  onClick?: () => void
  className?: string
  preferLow?: boolean
  thresholds?: number[]
}) => {
  if (value == undefined || value === -1) {
    return (
      <Badge onClick={onClick} className={className} variant="gray">
        No data
      </Badge>
    )
  }
  if (value < thresholds[0]!) {
    return (
      <Badge onClick={onClick} className={className} variant={preferLow ? "green" : "red"}>
        Low
      </Badge>
    )
  }
  if (value > thresholds[0]! && value < thresholds[1]!) {
    return (
      <Badge onClick={onClick} className={className} variant="yellow">
        Medium
      </Badge>
    )
  }
  if (value > thresholds[1]!) {
    return (
      <Badge onClick={onClick} className={className} variant={preferLow ? "red" : "green"}>
        High
      </Badge>
    )
  }
}

export const SignalRatingCell = ({
  value,
  className,
  onClick,
  preferLow,
  thresholds = [0.25, 0.5],
}: {
  value: number | undefined
  className?: string
  onClick?: () => void
  preferLow?: boolean
  thresholds?: number[]
}) => {
  if (value == undefined || value === -1) {
    return (
      <div className={cn(className, "flex flex-row gap-x-2 items-center")} onClick={onClick}>
        <SignalIcon variant="empty" preferLow={preferLow} />
        No data
      </div>
    )
  }
  if (value < thresholds[0]!) {
    return (
      <div className={cn(className, "flex flex-row gap-x-2 items-center")} onClick={onClick}>
        <SignalIcon variant="low" preferLow={preferLow} />
        Low
      </div>
    )
  }
  if (value > thresholds[0]! && value < thresholds[1]!) {
    return (
      <div className={cn(className, "flex flex-row gap-x-2 items-center")} onClick={onClick}>
        <SignalIcon variant="medium" preferLow={preferLow} />
        Medium
      </div>
    )
  }
  if (value > thresholds[1]!) {
    return (
      <div className={cn(className, "flex flex-row gap-x-2 items-center")} onClick={onClick}>
        <SignalIcon variant="full" preferLow={preferLow} />
        High
      </div>
    )
  }
}
